import React, { useEffect, useState } from "react";
import {
  findGetParameter,
  getData,
  postData,
  titleCase,
  validateMetric,
} from "../../../components/utilities/UtilityFunctions";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import { getTicketing, globalVariables } from "../../../global/GlobalVariables";
import { useInView } from "react-intersection-observer";
import { useConfirm } from "material-ui-confirm";
import {
  getGameSystems,
  handlePagingAll,
} from "../../../components/api/APIUtilities";
import { postNotification } from "../../../components/utilities/Notifications";
import {
  getEvent,
  getEventTemplates,
  markdownToPlainText,
  officialTemplateType,
  otherTemplateType,
} from "./EventFunctions";
import { newEventParallelData } from "../utils/event";
import { getEnvironmentVariable } from "../../../global/environmentVariables";
import moment from "moment";
import { uploadBas64ToS3 } from "../../../components/utilities/ImageUploader";
import EventCreationDefault from "./EventCreation/EventCreationDefault";
import {
  Backdrop,
  Box,
  Button,
  Container,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Link,
  Modal,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  BCPAngleLeft,
  BCPAngleRight,
  BCPCircle1,
  BCPCircle2,
  BCPCircle3,
  BCPCircle4,
  BCPXmark,
} from "../../../components/icons/bcp/BCPIcons";
import GenericPicker from "../../../components/utilities/GenericPicker";
import ModalLoadingFull from "../../../components/modals/ModalLoadingFull";
import GameRouter from "../../pairings/GameRouter";

const IS_BCP = getEnvironmentVariable("IS_BCP") === "true";
const IS_UY = getEnvironmentVariable("IS_UY") === "true";

export default function EventCreation(props) {
  const { event, setEvent, authContext } = props;
  const globalVars = globalVariables();

  const copyId = findGetParameter("copyId");
  const tier = findGetParameter("tier");

  let { eventId } = useParams();

  /*
    Step 0 - Select an org/gamestore (if user has any)
    Step 1 - Select game system
    Step 2 - Event Details 
    Step 3 - Game Configuration
    Step 4 - Registration and tickets
    Step 5 - Circuits
  */

  const [step, setStep] = useState(props?.step ? props.step : null);

  const [eventValues, setEventValues] = useState(null);

  const [isCustom, setIsCustom] = useState(false);

  const [readOnly, setReadOnly] = useState(
    event && !event?.userRole?.permissions?.eventDetails
  );
  const [copyEvent, setCopyEvent] = useState(null);
  const [gameStores, setGameStores] = useState(null);
  const [gameStoreDict, setGameStoreDict] = useState({});
  const [gameStore, setGameStore] = useState(null);
  const [gameSystems, setGameSystems] = useState(null);
  const [gameSystem, setGameSystem] = useState(null);
  const [circuits, setCircuits] = useState(null);

  const [eventSubType, setEventSubType] = useState("");

  const [user, setUser] = useState(null);

  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const [headerRef, headerInView] = useInView({
    threshold: 0,
  });

  const [timeZone, setTimeZone] = useState(null);
  const [locations, setLocations] = useState(null);
  const [scorecards, setScorecards] = useState(null);
  const [teamScorecards, setTeamScorecards] = useState(null);
  const [scorecardDict, setScorecardDict] = useState({});
  const [scorecardAdvancedOpen, setScorecardAdvancedOpen] = useState(false);

  const [reoccurringOpen, setReoccurringOpen] = useState(false);

  const [hasTicketingAccess, setHasTicketingAccess] = useState(false);

  const [templates, setTemplates] = useState(null);
  const [template, setTemplate] = useState(null);
  const [tempModalOpen, setTempModalOpen] = useState(false);

  const [scorecardModalOpen, setScorecardModalOpen] = useState(false);

  const [circuitsRequested, setCircuitsRequested] = useState([]);

  const confirm = useConfirm();
  const navigate = useNavigate();
  const theme = useTheme();
  const ticketingDict = getTicketing();

  const hasSubAccess =
    !IS_BCP ||
    authContext?.authState?.identity?.isAdmin === true ||
    authContext?.authState?.identity?.adminGameSystems?.includes(
      event?.gameSystemId
    ) ||
    (!event && authContext?.authState?.identity?.subscriber) ||
    event?.owner?.subscribed ||
    event?.authState?.identity?.isAdmin;

  const onDragEnd = (move, result) => {
    if (!result.destination) {
      return;
    }

    move(result.source.index, result.destination.index);
  };

  const fetchGameStores = async () => {
    if (gameStores) return;
    var storesArray = [];
    var storesDict = {};

    try {
      var apiStores = await handlePagingAll("/gamestores", {
        active: true,
        myStores: true,
        "expand[]": ["bankAccount"],
      });

      if (IS_UY) {
        let subbedStores = await getData(`/events/availableorgs`);

        if (subbedStores?.length) {
          let allowSelf = authContext?.authState?.identity?.isAdmin
            ? true
            : false;

          subbedStores.forEach((subbedStore) => {
            if (subbedStore.id === "self") {
              allowSelf = true;
            }

            if (apiStores?.length > 0) {
              apiStores.forEach((apiStore) => {
                if (subbedStore.id === apiStore.id) {
                  storesDict[subbedStore.id] = apiStore;

                  storesArray.push({
                    value: apiStore.id,
                    label: apiStore.name,
                  });
                }
              });
            }
          });

          if (storesArray?.length) {
            storesArray.sort((a, b) => {
              var nameA = a.label.toLowerCase();
              var nameB = b.label.toLowerCase();
              if (nameA > nameB) return 1;
              if (nameA < nameB) return -1;
              return 0;
            });
          }

          if (allowSelf) {
            storesArray.unshift({
              value: "self",
              label: "Self (No Organization)",
            });
          }

          setGameStoreDict(storesDict);
        }
      } else {
        if (apiStores?.length > 0) {
          var gameStoreIds = [];
          apiStores.forEach((apiStore) => {
            gameStoreIds.push(apiStore?.id);
          });

          try {
            var apiStoreUsers = await handlePagingAll("/gamestoreusers", {
              myStores: true,
              gameStores: JSON.stringify(gameStoreIds),
              "expand[]": ["gameStore"],
            });

            apiStoreUsers.forEach((apiStoreUser) => {
              if (
                apiStoreUser.gameStore &&
                (apiStoreUser.gameStore.ownerId ===
                  authContext?.authState?.identity?.userId ||
                  apiStoreUser.isTO === true) &&
                !storesDict[apiStoreUser.gameStore?.id]
              ) {
                storesArray.push({
                  value: apiStoreUser.gameStore.id,
                  label: apiStoreUser.gameStore.name,
                });

                apiStores.forEach((gameStore) => {
                  if (apiStoreUser.gameStore?.id === gameStore?.id) {
                    storesDict[apiStoreUser.gameStore.id] = gameStore;
                  }
                });
              }
            });

            if (storesArray.length > 0) {
              setGameStoreDict(storesDict);
              storesArray.sort((a, b) => {
                var nameA = a.label.toLowerCase();
                var nameB = b.label.toLowerCase();
                if (nameA > nameB) return 1;
                if (nameA < nameB) return -1;
                return 0;
              });

              storesArray = [
                {
                  value: "self",
                  label: "Self (No Organization)",
                },
                ...storesArray,
              ];
            }
          } catch (err) {
            postNotification("error", err);
          }
        }
      }
    } catch (err) {
      postNotification("error", err);
    }

    setGameStores(storesArray);

    return storesArray;
  };

  const fetchLocations = async () => {
    if (locations) {
      return locations;
    }
    try {
      var apiLocations = await handlePagingAll("/locations", {
        userId: authContext?.authState?.identity?.userId,
      });
      apiLocations.sort((a, b) => {
        if (event?.locationId) {
          if (a.id === event.locationId) return 1;
          if (b.id === event.locationId) return -1;
        }
        if (a.name && b.name) {
          var nameA = a.name.toLowerCase();
          var nameB = b.name.toLowerCase();
          if (nameA > nameB) return 1;
          if (nameA < nameB) return -1;
        } else if (a.name) {
          return 1;
        } else if (b.name) {
          return -1;
        }
        return 0;
      });

      return apiLocations;
    } catch (err) {
      postNotification("error", err);
    }
    return [];
  };

  const fetchUser = async (userId) => {
    if (user) {
      return user;
    }
    var returnObj;
    try {
      returnObj = await getData(
        `/users/${
          userId
          // eventObj?.ownerId || authContext?.authState?.identity?.userId
        }`,
        {
          queryStringParameters: {
            "expand[]": ["bankAccount"],
          },
        }
      );
    } catch (err) {
      console.error(err);
    }
    return returnObj;
  };

  const fetchEvent = async (eventId) => {
    if (!eventId) return;

    var returnObj;
    try {
      returnObj = await getEvent(eventId, authContext, {
        player: false,
        photo: true,
        role: true,
        ticketing: true,
      });
    } catch (err) {
      console.error(err);
    }
    return returnObj;
  };

  const fetchLeagues = async (gsystem) => {
    let queryParams = {
      public: true,
      active: true,
      sortKey: "name",
      gameSystemId: gsystem.id,
    };

    var leagueData = await handlePagingAll("/leagues", queryParams);

    if (leagueData) {
      leagueData.sort((a, b) => {
        var nameA = a.name.toLowerCase();
        var nameB = b.name.toLowerCase();
        if (nameA > nameB) return 1;
        if (nameA < nameB) return -1;
        return 0;
      });
      return leagueData;
    } else return [];
  };

  const fetchTokens = async () => {
    if (!event) {
      return [];
    }
    let queryParamsTokens = {
      limit: 500,
      eventId: event.id,
      userId: authContext?.authState?.identity?.userId,
      "expand[]": ["league"],
    };

    return await handlePagingAll("/leaguetokens", queryParamsTokens);
  };

  const fetchScorecards = async (gsystem) => {
    let queryParams = {
      gameSystemId: gsystem.id,
    };
    if (authContext?.authState?.identity?.userId) {
      queryParams.ownerId = authContext?.authState?.identity?.userId;
    } else {
      queryParams.public = true;
    }
    if (gsystem.boardGameEvent) {
      queryParams.type = "SeatedPairing";
    }

    return await handlePagingAll("/scorecards", queryParams);
  };

  const parallelPostGameSystemData = async (gsystem) => {
    const taskTokens = fetchTokens();
    const taskScorecards = fetchScorecards(gsystem);
    const taskLeagues = fetchLeagues(gsystem);

    return {
      scorecards: await taskScorecards,
      leagues: await taskLeagues,
      tokens: await taskTokens,
    };
  };

  const pageSetup = async () => {
    var apiCopyEvent = undefined;
    var apiGameSystems = undefined;
    var apiUser = undefined;
    var apiLocations = undefined;

    if (event) {
      if (event?.gameStoreId || event?.gameStore?.id) {
        try {
          const apiGameStore = await getData(
            `/gamestores/${event?.gameStoreId || event?.gameStore?.id}`,
            { "expand[]": ["bankAccount"] }
          );
          if (apiGameStore) {
            setGameStore(apiGameStore);
            event.gameStore = apiGameStore;
          }
        } catch (err) {
          postNotification("error", err);
        }
      }

      let gameSystemDetails = null;
      if (event?.gameSystem?.id || event?.gameSystemId) {
        try {
          const apiGameSystem = await getData(
            `/gamesystems/${event?.gameSystem?.id || event?.gameSystemId}`
          );

          if (apiGameSystem) {
            gameSystemDetails = apiGameSystem;
          }
        } catch (err) {
          postNotification("error", err);
        }
      }

      setGameSystem({
        boardGameEvent: event.boardGameEvent,
        ...(gameSystemDetails || event.gameSystem),
      });
    } else if (copyId) {
      // get copy event
      apiCopyEvent = await fetchEvent(copyId);

      if (apiCopyEvent?.gameStoreId || apiCopyEvent?.gameStore?.id) {
        try {
          const apiGameStore = await getData(
            `/gamestores/${
              apiCopyEvent?.gameStoreId || apiCopyEvent?.gameStore?.id
            }`,
            { "expand[]": ["bankAccount"] }
          );
          if (apiGameStore) {
            setGameStore(apiGameStore);
            apiCopyEvent.gameStore = apiGameStore;
          }
        } catch (err) {
          postNotification("error", err);
        }
      }

      let gameSystemDetails = null;
      if (apiCopyEvent?.gameSystem?.id || apiCopyEvent?.gameSystemId) {
        try {
          const apiGameSystem = await getData(
            `/gamesystems/${
              apiCopyEvent.gameSystem.id || apiCopyEvent.gameSystemId
            }`
          );

          if (apiGameSystem) {
            gameSystemDetails = apiGameSystem;
          }
        } catch (err) {
          postNotification("error", err);
        }
      }

      if (apiCopyEvent) {
        setCopyEvent(apiCopyEvent);
        setGameSystem({
          boardGameEvent: apiCopyEvent.boardGameEvent,
          ...(gameSystemDetails || apiCopyEvent.gameSystem),
        });
      }
      // setStep(3);
    } else {
      const parallelData = await newEventParallelData(
        authContext?.authState?.identity?.userId,
        fetchGameStores,
        getGameSystems,
        fetchUser,
        fetchLocations
      );

      const apiGameStores = parallelData.apiGameStores;
      setStep(apiGameStores?.length ? 0 : 1);

      apiGameSystems = parallelData.apiGameSystems;
      setGameSystems(apiGameSystems);

      if (getEnvironmentVariable("SINGLE_GAME_SYSTEM")) {
        setGameSystem(
          apiGameSystems.filter(
            (el) => el.id === getEnvironmentVariable("SINGLE_GAME_SYSTEM")
          )[0]
        );
      }

      apiUser = parallelData.apiUser;
      apiLocations = parallelData.apiLocations;
    }

    let apiEventSubType =
      event?.eventSubType && event?.eventSubType !== ""
        ? event.eventSubType
        : apiCopyEvent?.eventSubType && apiCopyEvent?.eventSubType !== ""
          ? apiCopyEvent.eventSubType
          : globalVars.eventSubTypes?.list?.length === 1
            ? globalVars.eventSubTypes?.list?.[0]
            : tier || null;
    setEventSubType(apiEventSubType);

    if (!apiUser && (event?.ownerId || apiCopyEvent?.ownerId)) {
      apiUser = await fetchUser(event?.ownerId || apiCopyEvent?.ownerId);
    }
    setUser(apiUser);
    setLoading(false);

    if (!apiLocations) {
      apiLocations = await fetchLocations();
    }
    if (event || apiCopyEvent) {
      if (
        (event?.locationId && event?.location) ||
        (apiCopyEvent?.locationId && apiCopyEvent?.location)
      ) {
        setTimeZone(
          event?.location?.timeZone || apiCopyEvent?.location?.timeZone
        );
        if (
          apiLocations &&
          apiLocations.filter(
            (e) =>
              e.id === event?.locationId || e.id === apiCopyEvent?.locationId
          ).length === 0
        ) {
          apiLocations = [
            event?.location || apiCopyEvent?.location,
            ...apiLocations,
          ];
        }
      }
    }
    setLocations(apiLocations);
  };

  const postGameSystem = async (gsystem) => {
    if (gsystem) {
      try {
        let apiData = await parallelPostGameSystemData(gsystem);
        let circuitDict = {};
        if (apiData.leagues?.length > 0) {
          for (let circuit of apiData.leagues) {
            circuitDict[circuit.id] = { ...circuit, tokens: [] };
          }
        }
        if (apiData.tokens?.length > 0) {
          for (let token of apiData.tokens) {
            if (token.leagueId) {
              if (!circuitDict[token.leagueId]) {
                circuitDict[token.leagueId] = { ...token.league, tokens: [] };
              }
              if (circuitDict[token.leagueId]?.tokens) {
                circuitDict[token.leagueId].tokens.push(token);
              }
            }
          }
        }
        let _circuits = Object.values(circuitDict);
        if (_circuits?.length > 0) {
          _circuits.sort((a, b) => {
            var nameA = a.name.toLowerCase();
            var nameB = b.name.toLowerCase();
            if (nameA > nameB) return 1;
            if (nameA < nameB) return -1;
            return 0;
          });
        }
        setCircuits(_circuits);
        var apiScorecards = apiData.scorecards;

        var apiScorecardDict = {};

        var foundCurrent = false;
        var foundDefault = false;
        var scorecardArray = [];

        var foundTeamDefault = false;
        var foundTeamCurrent = false;
        var teamScorecardArray = [];

        apiScorecards.forEach((scorecard) => {
          apiScorecardDict[scorecard.id] = scorecard;
          if (event) {
            if (event.scorecardId === scorecard.id) {
              foundCurrent = true;
            }
            if (event.teamScorecardId === scorecard.id) {
              foundTeamCurrent = true;
            }
          } else if (copyEvent) {
            if (copyEvent.scorecardId === scorecard.id) {
              foundCurrent = true;
            }
            if (copyEvent.teamScorecardId === scorecard.id) {
              foundTeamCurrent = true;
            }
          }
          if (gsystem.scorecardId === scorecard.id) {
            foundDefault = true;
          }
          if (gsystem.teamScorecardId === scorecard.id) {
            foundTeamDefault = true;
          }

          if (
            scorecard.type === "Pairing" ||
            scorecard.type === "SeatedPairing"
          ) {
            scorecardArray.push({
              value: scorecard.id,
              label: scorecard.name,
              default: gsystem.scorecardId === scorecard.id,
              current:
                event?.scorecardId === scorecard.id ||
                copyEvent?.scorecardId === scorecard.id,
              scorecardType: scorecard.scorecardType,
            });
          } else if (scorecard.type === "TeamPairing") {
            teamScorecardArray.push({
              value: scorecard.id,
              label: scorecard.name,
              default: gsystem.teamScorecardId === scorecard.id,
              current:
                event?.teamScorecardId === scorecard.id ||
                copyEvent?.teamScorecardId === scorecard.id,
              scorecardType: scorecard.scorecardType,
            });
          }
        });

        if (
          !foundCurrent &&
          ((event?.scorecardId &&
            event?.scorecardId !== gsystem?.scorecardId) ||
            (copyEvent?.scorecardId &&
              copyEvent?.scorecardId !== gsystem?.scorecardId))
        ) {
          scorecardArray.push({
            value: event?.scorecardId || copyEvent?.scorecardId,
            label:
              event?.scorecard?.name ||
              event?.name ||
              copyEvent?.scorecard?.name ||
              copyEvent?.name,
            current: true,
            scorecardType:
              event?.scorecard?.scorecardType ||
              copyEvent?.scorecard?.scorecardType,
          });
        }
        if (!foundDefault) {
          scorecardArray.push({
            value: "Default Scorecard",
            label: "Default Scorecard",
            default: true,
          });
        }

        if (
          !foundTeamCurrent &&
          ((event?.teamScorecardId &&
            event?.teamScorecardId !== gsystem?.teamScorecardId) ||
            (copyEvent?.teamScorecardId &&
              copyEvent?.teamScorecardId !== gsystem?.teamScorecardId))
        ) {
          teamScorecardArray.push({
            value: event?.teamScorecardId || copyEvent?.teamScorecardId,
            label:
              event?.teamScorecard?.name ||
              event?.name ||
              copyEvent?.teamScorecard?.name ||
              copyEvent?.name,
            current: true,
            scorecardType:
              event?.teamScorecard?.scorecardType ||
              copyEvent?.teamScorecard?.scorecardType,
          });
        }
        if (!foundTeamDefault) {
          teamScorecardArray.push({
            value: "Default Scorecard",
            label: "Default Scorecard",
            default: true,
          });
        }

        scorecardArray.sort((a, b) => {
          if (a.default) return -1;
          if (b.default) return 1;
          if (a.current) return -1;
          if (b.current) return 1;
          var nameA = a.label.toLowerCase();
          var nameB = b.label.toLowerCase();
          if (nameA > nameB) return 1;
          if (nameA < nameB) return -1;
          return 0;
        });
        setScorecards(scorecardArray);

        teamScorecardArray.sort((a, b) => {
          if (a.default) return -1;
          if (b.default) return 1;
          if (a.current) return -1;
          if (b.current) return 1;
          var nameA = a.label.toLowerCase();
          var nameB = b.label.toLowerCase();
          if (nameA > nameB) return 1;
          if (nameA < nameB) return -1;
          return 0;
        });
        setTeamScorecards(teamScorecardArray);

        setScorecardDict(apiScorecardDict);
      } catch (err) {
        postNotification("error", err);
      }
    }
  };

  const getInitialValues = (rawValues = false) => {
    let eventCurrency =
      event?.ticketCurrency ||
      copyEvent?.ticketCurrency ||
      Object.keys(
        event?.pricingDict ||
          copyEvent?.pricingDict || {
            [event?.availableCurrencies?.length > 0
              ? event.availableCurrencies[0]
              : user?.bankAccount?.availableCurrencies?.length > 0
                ? user.bankAccount.availableCurrencies[0]
                : "usd"]: 0,
          }
      )[0];
    let userCurrency =
      event?.ownerId === user?.id || !event?.id
        ? user?.bankAccount?.availableCurrencies[0] || "usd"
        : "usd";

    // handle game store currency

    let currency = (
      event?.ticketPrice > 0 || copyEvent?.ticketPrice > 0
        ? eventCurrency
        : userCurrency
    ).toLowerCase();

    let pricingDict =
      event?.ticketPrice > 0
        ? event?.pricingDict
        : copyEvent?.ticketPrice > 0
          ? copyEvent?.pricingDict
          : { [userCurrency]: 0 };

    let ticketingValues = {
      playerPaysFees:
        event?.playerPaysFees !== undefined
          ? event.playerPaysFees
          : copyEvent?.playerPaysFees !== undefined
            ? copyEvent.playerPaysFees
            : false,
      availableCurrencies: [currency],
      pricingDict: pricingDict,
      ticketingFees: event?.ticketingFees,
      ticketPrice:
        (event?.pricingDict && Object.keys(event.pricingDict).length > 0
          ? event.pricingDict[Object.keys(event.pricingDict)[0]]
          : event?.ticketPrice !== undefined
            ? event.ticketPrice
            : copyEvent?.pricingDict &&
                Object.keys(copyEvent.pricingDict).length > 0
              ? copyEvent.pricingDict[Object.keys(copyEvent.pricingDict)[0]]
              : copyEvent?.ticketPrice !== undefined
                ? copyEvent.ticketPrice
                : 0) / 100,
      ticketCurrency: currency.toLowerCase(),
      currency: currency,
    };

    ticketingValues.amount = ticketingValues.ticketPrice;

    let initialValues = {
      name: event?.name || copyEvent?.name || "",
      location:
        event?.gameStoreName ||
        event?.gameStore?.name ||
        gameStore?.location?.name ||
        null,
      locationId:
        event?.locationId ||
        copyEvent?.locationId ||
        gameStore?.location?.id ||
        "",
      isOnlineEvent: event
        ? event?.isOnlineEvent
        : copyEvent
          ? copyEvent?.isOnlineEvent
          : false,
      sponsored: event ? event?.sponsored : false,
      featured: event ? event?.featured : false,

      privateEvent: event?.privateEvent || copyEvent?.privateEvent || false,

      photoFileId: event?.photoFileId || copyEvent?.photoFileId || null,

      pairingStyle: titleCase(
        event?.pairingStyle || copyEvent?.pairingStyle || "swiss"
      ),
      requirePairingPublish:
        event?.requirePairingPublish ||
        copyEvent?.requirePairingPublish ||
        false,
      numberOfRounds:
        event?.numberOfRounds !== undefined
          ? event.numberOfRounds
          : copyEvent?.numberOfRounds !== undefined
            ? copyEvent.numberOfRounds
            : gameSystem?.roundsLogBase2
              ? 0
              : 3,
      eventDate:
        event?.eventDate && (event?.location?.timeZone || timeZone)
          ? moment(event.eventDate)
              .tz(event?.location?.timeZone || timeZone)
              .format("YYYY-MM-DDTHH:mm")
          : event?.eventDate
            ? moment(event.eventDate).format("YYYY-MM-DDTHH:mm")
            : copyEvent?.eventDate &&
                (copyEvent?.location?.timeZone || timeZone)
              ? moment()
                  .add(
                    moment(copyEvent.eventDate).day() >= moment().day()
                      ? moment(copyEvent.eventDate).day() - moment().day()
                      : moment(copyEvent.eventDate).day() - moment().day() + 7,
                    "days"
                  )
                  .set({
                    hour: moment(copyEvent.eventDate).hour(),
                    minute: moment(copyEvent.eventDate).minute(),
                  })
                  .tz(copyEvent?.location?.timeZone || timeZone)
                  .format("YYYY-MM-DDTHH:mm")
              : copyEvent?.eventDate
                ? moment()
                    .add(
                      moment(copyEvent.eventDate).day() >= moment().day()
                        ? moment(copyEvent.eventDate).day() - moment().day()
                        : moment(copyEvent.eventDate).day() -
                            moment().day() +
                            7,
                      "days"
                    )
                    .set({
                      hour: moment(copyEvent.eventDate).hour(),
                      minute: moment(copyEvent.eventDate).minute(),
                    })
                    .format("YYYY-MM-DDTHH:mm")
                : moment(
                    moment().format("YYYY-MM-DD") + " 10:00",
                    "YYYY-MM-DD HH:mm"
                  ).format("YYYY-MM-DDTHH:mm"),
      eventEndDate:
        event?.eventEndDate && (event?.location?.timeZone || timeZone)
          ? moment(event.eventEndDate)
              .tz(event?.location?.timeZone || timeZone)
              .format("YYYY-MM-DDTHH:mm")
          : event?.eventEndDate
            ? moment(event.eventEndDate).format("YYYY-MM-DDTHH:mm")
            : copyEvent?.eventEndDate &&
                (copyEvent?.location?.timeZone || timeZone)
              ? moment()
                  .add(
                    moment(copyEvent.eventEndDate).day() >= moment().day()
                      ? moment(copyEvent.eventEndDate).day() - moment().day()
                      : moment(copyEvent.eventEndDate).day() -
                          moment().day() +
                          7,
                    "days"
                  )
                  .set({
                    hour: moment(copyEvent.eventEndDate).hour(),
                    minute: moment(copyEvent.eventEndDate).minute(),
                  })
                  .tz(copyEvent?.location?.timeZone || timeZone)
                  .format("YYYY-MM-DDTHH:mm")
              : copyEvent?.eventEndDate
                ? moment()
                    .add(
                      moment(copyEvent.eventEndDate).day() >= moment().day()
                        ? moment(copyEvent.eventEndDate).day() - moment().day()
                        : moment(copyEvent.eventEndDate).day() -
                            moment().day() +
                            7,
                      "days"
                    )
                    .set({
                      hour: moment(copyEvent.eventEndDate).hour(),
                      minute: moment(copyEvent.eventEndDate).minute(),
                    })
                    .format("YYYY-MM-DDTHH:mm")
                : moment(
                    moment().format("YYYY-MM-DD") + " 19:00",
                    "YYYY-MM-DD HH:mm"
                  ).format("YYYY-MM-DDTHH:mm"),

      // reoccurring event features only available on new events
      reoccurringEvent: false,
      reoccurringDates: [],

      usingOnlineReg: event
        ? event?.usingOnlineReg
        : copyEvent
          ? copyEvent?.usingOnlineReg
          : false,
      disableCheckin: event
        ? event?.disableCheckin
        : copyEvent
          ? copyEvent?.disableCheckin
          : false,

      numTickets: event?.numTickets || copyEvent?.numTickets || 0,
      refundDate:
        event?.refundDate && (event?.location?.timeZone || timeZone)
          ? moment(event.refundDate)
              .tz(event?.location?.timeZone || timeZone)
              .format("YYYY-MM-DDTHH:mm")
          : event?.refundDate
            ? moment(event.refundDate).format("YYYY-MM-DDTHH:mm")
            : copyEvent?.refundDate &&
                (copyEvent?.location?.timeZone || timeZone)
              ? moment()
                  .add(
                    moment(copyEvent.refundDate).day() >= moment().day()
                      ? moment(copyEvent.refundDate).day() - moment().day()
                      : moment(copyEvent.refundDate).day() - moment().day() + 7,
                    "days"
                  )
                  .set({
                    hour: moment(copyEvent.refundDate).hour(),
                    minute: moment(copyEvent.refundDate).minute(),
                  })
                  .tz(copyEvent?.location?.timeZone || timeZone)
                  .format("YYYY-MM-DDTHH:mm")
              : copyEvent?.refundDate
                ? moment()
                    .add(
                      moment(copyEvent.refundDate).day() >= moment().day()
                        ? moment(copyEvent.refundDate).day() - moment().day()
                        : moment(copyEvent.refundDate).day() -
                            moment().day() +
                            7,
                      "days"
                    )
                    .set({
                      hour: moment(copyEvent.refundDate).hour(),
                      minute: moment(copyEvent.refundDate).minute(),
                    })
                    .format("YYYY-MM-DDTHH:mm")
                : moment().endOf("day").format("YYYY-MM-DDTHH:mm"),

      playerPaysFees:
        event?.playerPaysFees !== undefined
          ? event.playerPaysFees
          : copyEvent?.playerPaysFees !== undefined
            ? copyEvent.playerPaysFees
            : false,
      availableCurrencies: event?.availableCurrencies ||
        copyEvent?.availableCurrencies ||
        user?.bankAccount?.availableCurrencies || ["usd"],
      pricingDict: event?.pricingDict ||
        copyEvent?.pricingDict || {
          [event?.availableCurrencies?.length > 0
            ? event.availableCurrencies[0]
            : user?.bankAccount?.availableCurrencies?.length > 0
              ? user.bankAccount.availableCurrencies[0]
              : "usd"]: 0,
        },
      ticketingFees: event?.ticketingFees,
      ticketPrice:
        (event?.ticketPrice !== undefined
          ? event.ticketPrice
          : event?.pricingDict && Object.keys(event.pricingDict).length > 0
            ? event.pricingDict[Object.keys(event.pricingDict)[0]]
            : copyEvent?.ticketPrice !== undefined
              ? copyEvent.ticketPrice
              : copyEvent?.pricingDict &&
                  Object.keys(copyEvent.pricingDict).length > 0
                ? copyEvent.pricingDict[Object.keys(copyEvent.pricingDict)[0]]
                : 0) / 100,
      ticketCurrency:
        event?.ticketCurrency ||
        Object.keys(
          event?.pricingDict ||
            copyEvent?.pricingDict || {
              [event?.availableCurrencies?.length > 0
                ? event.availableCurrencies[0]
                : user?.bankAccount?.availableCurrencies?.length > 0
                  ? user.bankAccount.availableCurrencies[0]
                  : "usd"]: 0,
            }
        )[0],

      eventDescription:
        event?.eventDescription || copyEvent?.eventDescription || "",
      eventDescriptionMarkup:
        event?.eventDescriptionMarkup ||
        copyEvent?.eventDescriptionMarkup ||
        event?.eventDescription ||
        copyEvent?.eventDescription ||
        "",

      scorecardId:
        event?.scorecardId ||
        event?.gameSystem?.scorecardId ||
        copyEvent?.scorecardId ||
        copyEvent?.gameSystem?.scorecardId ||
        gameSystem?.scorecardId ||
        "",
      teamScorecardId:
        event?.teamScorecardId ||
        event?.gameSystem?.teamScorecardId ||
        copyEvent?.teamScorecardId ||
        copyEvent?.gameSystem?.teamScorecardId ||
        gameSystem?.teamScorecardId ||
        "",
      defaultByeGameData: event
        ? event.defaultByeGameData
        : copyEvent
          ? copyEvent.defaultByeGameData
          : null,

      doublesEvent: event
        ? event.doublesEvent
        : copyEvent
          ? copyEvent.doublesEvent
          : false,
      teamEvent: event
        ? event.teamEvent
        : copyEvent
          ? copyEvent.teamEvent
          : false,
      matchWinsForTie:
        event?.matchWinsForTie !== undefined
          ? event.matchWinsForTie
          : copyEvent?.matchWinsForTie !== undefined
            ? copyEvent.matchWinsForTie
            : 0,
      marginOfVictoryForTie:
        event?.marginOfVictoryForTie !== undefined
          ? event.marginOfVictoryForTie
          : copyEvent?.marginOfVictoryForTie !== undefined
            ? copyEvent.marginOfVictoryForTie
            : 0,
      marginOfVictoryForWin:
        event?.marginOfVictoryForWin !== undefined
          ? event.marginOfVictoryForWin
          : copyEvent?.marginOfVictoryForWin !== undefined
            ? copyEvent.marginOfVictoryForWin
            : 0,
      gamePointsForWin:
        event?.gamePointsForWin !== undefined
          ? event.gamePointsForWin
          : copyEvent?.gamePointsForWin !== undefined
            ? copyEvent.gamePointsForWin
            : 0,
      metric1Band1:
        event?.metric1Band1 !== undefined
          ? event.metric1Band1
          : copyEvent?.metric1Band1 !== undefined
            ? copyEvent.metric1Band1
            : 0,
      metric1Band2:
        event?.metric1Band2 !== undefined
          ? event.metric1Band2
          : copyEvent?.metric1Band2 !== undefined
            ? copyEvent.metric1Band2
            : 0,
      metric1Band3:
        event?.metric1Band3 !== undefined
          ? event.metric1Band3
          : copyEvent?.metric1Band3 !== undefined
            ? copyEvent.metric1Band3
            : 0,
      leagueEvent:
        event?.leagueEvent !== undefined
          ? event.leagueEvent
          : copyEvent?.leagueEvent !== undefined
            ? copyEvent.leagueEvent
            : false,
      eventType:
        event?.doublesEvent || copyEvent?.doublesEvent
          ? "Doubles Event"
          : event?.teamEvent || copyEvent?.teamEvent
            ? "Teams Event"
            : "Singles Event",

      placingRecordType: event?.placingRecordType ||
        copyEvent?.placingRecordType ||
        gameSystem?.placingRecordTypes?.[0] || { key: "", name: "" },
      pairingMetrics:
        event?.pairingMetrics ||
        copyEvent?.pairingMetrics ||
        gameSystem?.defaultPairingsMetrics ||
        [],
      placingMetrics:
        event?.placingMetrics ||
        copyEvent?.placingMetrics ||
        gameSystem?.defaultPlacingMetrics ||
        [],
      playerPlacingMetrics:
        event?.playerPlacingMetrics || copyEvent?.playerPlacingMetrics || [],

      displayOverall:
        event?.displayOverall !== undefined
          ? event.displayOverall
          : copyEvent?.displayOverall !== undefined
            ? copyEvent.displayOverall
            : true,
      rankedTables:
        event?.rankedTables !== undefined
          ? event.rankedTables
          : copyEvent?.rankedTables !== undefined
            ? copyEvent.rankedTables
            : gameSystem?.defaultRankedTables === true
              ? true
              : false,
      startingTable:
        event?.startingTable !== undefined
          ? event.startingTable
          : copyEvent?.startingTable !== undefined
            ? copyEvent.startingTable
            : 1,
      hidePlacings:
        event?.hidePlacings !== undefined
          ? event.hidePlacings
          : copyEvent?.hidePlacings !== undefined
            ? copyEvent.hidePlacings
            : gameSystem?.hidePlacingsDefault !== undefined
              ? gameSystem.hidePlacingsDefault
              : false,
      hideRoster:
        event?.hideRoster !== undefined
          ? event.hideRoster
          : copyEvent?.hideRoster !== undefined
            ? copyEvent.hideRoster
            : false,
      hidePlayerCount:
        event?.hidePlayerCount !== undefined
          ? event.hidePlayerCount
          : copyEvent?.hidePlayerCount !== undefined
            ? copyEvent.hidePlayerCount
            : false,
      defaultRoundLength: {
        hrs: event?.defaultRoundLength
          ? Math.floor(event.defaultRoundLength / 60 / 60)
          : copyEvent?.defaultRoundLength
            ? Math.floor(copyEvent.defaultRoundLength / 60 / 60)
            : gameSystem?.defaultRoundLength
              ? Math.floor(gameSystem.defaultRoundLength / 60 / 60)
              : 2,
        mins: event?.defaultRoundLength
          ? Math.floor(event.defaultRoundLength / 60) % 60
          : copyEvent?.defaultRoundLength
            ? Math.floor(copyEvent.defaultRoundLength / 60) % 60
            : gameSystem?.defaultRoundLength
              ? Math.floor(gameSystem.defaultRoundLength / 60) % 60
              : 30,
      },

      enablePasswords:
        event?.enablePasswords !== undefined
          ? event.enablePasswords
          : copyEvent?.enablePasswords !== undefined
            ? copyEvent.enablePasswords
            : true,
      passwordlessScoring:
        event?.passwordlessScoring !== undefined
          ? event?.passwordlessScoring
          : copyEvent?.passwordlessScoring !== undefined
            ? copyEvent?.passwordlessScoring
            : true,

      hideLists: event
        ? event.hideLists
        : copyEvent
          ? copyEvent.hideLists
          : gameSystem
            ? gameSystem.hideListsDefault
            : true,

      allowTextOnly: event
        ? !event?.listOptions?.allowsImages
        : copyEvent
          ? !copyEvent?.listOptions?.allowsImages
          : true,
      listOptions: event?.listOptions
        ? event?.listOptions
        : copyEvent?.listOptions
          ? copyEvent?.listOptions
          : gameSystem?.textOnlyLists
            ? {
                allowsFiles: false,
                allowsImages: false,
                allowsText: true,
              }
            : {
                allowsFiles: true,
                allowsImages: true,
                allowsText: true,
              },

      listsAtCheckin:
        event?.listsAtCheckin !== undefined
          ? event.listsAtCheckin
          : copyEvent?.listsAtCheckin !== undefined
            ? copyEvent.listsAtCheckin
            : gameSystem?.listsAtCheckin !== undefined
              ? gameSystem.listsAtCheckin
              : false,
      listsLocked:
        event?.listsLocked !== undefined
          ? event.listsLocked
          : copyEvent?.listsLocked !== undefined
            ? copyEvent.listsLocked
            : false,
      listSubmissionLocked:
        event?.listSubmissionLocked !== undefined
          ? event.listSubmissionLocked
          : gameSystem?.listSubmissionLocked !== undefined
            ? gameSystem?.listSubmissionLocked
            : false,

      externalUrl:
        event?.externalUrl ||
        copyEvent?.externalUrl ||
        gameStore?.defaultExternalURL ||
        "",

      eventSubType:
        event?.eventSubType || copyEvent?.eventSubType || eventSubType || "",

      eventFormat:
        event?.eventFormat ||
        copyEvent?.eventFormat ||
        gameSystem?.eventFormats?.default ||
        "",

      shippingDetails: {
        requested:
          event?.shippingDetails?.requested !== undefined
            ? event?.shippingDetails?.requested
            : copyEvent?.shippingDetails?.requested !== undefined
              ? copyEvent?.shippingDetails?.requested
              : false,

        mandatory:
          event?.shippingDetails?.mandatory !== undefined
            ? event?.shippingDetails?.mandatory
            : copyEvent?.shippingDetails?.mandatory !== undefined
              ? copyEvent?.shippingDetails?.mandatory
              : false,

        description:
          event?.shippingDetails?.description !== undefined
            ? event?.shippingDetails?.description
            : copyEvent?.shippingDetails?.description !== undefined
              ? copyEvent?.shippingDetails?.description
              : "",
      },
      templateId: event?.templateId || copyEvent?.templateId || "default",
      templateName:
        event?.templateId || copyEvent?.templateId || "Default (recommended)",
      templateModified:
        event?.templateModified || copyEvent?.templateModified || false,
    };

    let nonNullFields = [
      "marginOfVictoryForTie",
      "matchWinsForTie",
      "marginOfVictoryForWin",
    ];
    for (let nonNullField of nonNullFields) {
      if (initialValues[nonNullField] === null) {
        initialValues[nonNullField] = 0;
      }
    }

    if (initialValues?.eventDate && initialValues?.eventEndDate) {
      if (
        moment(initialValues.eventEndDate).isBefore(initialValues.eventDate)
      ) {
        initialValues.eventEndDate = moment(initialValues.eventEndDate).add(
          7,
          "days"
        );
      }
    }

    if (
      initialValues?.pricingDict &&
      Object.keys(initialValues.pricingDict).length > 0
    ) {
      initialValues.currency = Object.keys(initialValues.pricingDict)[0];
    } else {
      initialValues.currency = "usd";
    }

    if (initialValues?.pricingDict) {
      initialValues.amount =
        initialValues.pricingDict[initialValues.currency] / 100;
    } else {
      initialValues.amount = 0;
    }

    if (!rawValues) {
      if (!eventValues) {
        setEventValues(initialValues);
      } else {
        initialValues = { ...initialValues, ...eventValues };
      }

      return initialValues;
    }
  };

  const getDetailsValidationSchema = () =>
    Yup.object().shape({
      name: Yup.string()
        .min(3, "Minimum 3 Characters!")
        .max(255, "Maximum 255 Characters!")
        .required("Required"),
      locationId: Yup.string().required("Required"),
      eventDate: Yup.string()
        .required("Required")
        .nullable()
        .test("Is valid date?", "Invalid event end date", (value) => {
          if (!value || value.trim() === "") {
            return false;
          }
          return moment(value, "YYYY-MM-DDTHH:mm", true).isValid();
        })
        .test(
          "Is valid new start date?",
          "Start date for new events must be no older than 12 months",
          (value) => {
            return (
              event?.id ||
              moment(value, "YYYY-MM-DDTHH:mm", true).isAfter(
                moment().subtract(12, "months")
              )
            );
          }
        )
        .test(
          "Is valid existing start date?",
          "Start date cannot be before 2016-02-06",
          (value) => {
            return (
              !event?.id ||
              moment(value, "YYYY-MM-DDTHH:mm", true).isAfter(
                moment("2016-02-06", "YYYY-MM-DD", true) // first LVO
              )
            );
          }
        ),
      eventEndDate: Yup.string()
        .required("Required")
        .nullable()
        .test("Is valid date?", "Invalid event end date", (value) => {
          if (!value || value.trim() === "") {
            return false;
          }
          return moment(value, "YYYY-MM-DDTHH:mm", true).isValid();
        }),
    });

  const getConfigValidationSchema = () =>
    Yup.object().shape({
      numberOfRounds:
        gameSystem?.roundsLogBase2 || event?.gameSystem?.roundsLogBase2
          ? Yup.number()
          : Yup.number().test(
              "Number-of-Rounds",
              "Invalid number of rounds",
              (value, context) => {
                if (
                  !context?.parent?.pairingStyle ||
                  context.parent.pairingStyle.toLowerCase() === "swiss"
                ) {
                  if (value === "" || value === null || value === undefined) {
                    return context.createError({
                      message: "Required",
                    });
                  }
                  if (
                    value <= 0 &&
                    !authContext?.authState?.identity?.isAdmin
                  ) {
                    return context.createError({
                      message: "The number of rounds must be greater than 0!",
                    });
                  }
                }
                return true;
              }
            ),
    });

  const getValidationSchema = () =>
    Yup.object().shape({
      name: Yup.string()
        .min(3, "Minimum 3 Characters!")
        .max(255, "Maximum 255 Characters!")
        .required("Required"),
      locationId: Yup.string().required("Required"),
      numberOfRounds:
        gameSystem?.roundsLogBase2 || event?.gameSystem?.roundsLogBase2
          ? Yup.number()
          : Yup.number().test(
              "Number-of-Rounds",
              "Invalid number of rounds",
              (value, context) => {
                if (
                  !context?.parent?.pairingStyle ||
                  context.parent.pairingStyle.toLowerCase() === "swiss"
                ) {
                  if (value === "" || value === null || value === undefined) {
                    return context.createError({
                      message: "Required",
                    });
                  }
                  if (
                    value <= 0 &&
                    !authContext?.authState?.identity?.isAdmin
                  ) {
                    return context.createError({
                      message: "The number of rounds must be greater than 0!",
                    });
                  }
                }
                return true;
              }
            ),
      eventDate: Yup.string()
        .required("Required")
        .nullable()
        .test("Is valid date?", "Invalid event end date", (value) => {
          if (!value || value.trim() === "") {
            return false;
          }
          return moment(value, "YYYY-MM-DDTHH:mm", true).isValid();
        })
        .test(
          "Is valid new start date?",
          "Start date for new events must be no older than 12 months",
          (value) => {
            return (
              event?.id ||
              moment(value, "YYYY-MM-DDTHH:mm", true).isAfter(
                moment().subtract(12, "months")
              )
            );
          }
        )
        .test(
          "Is valid existing start date?",
          "Start date cannot be before 2016-02-06",
          (value) => {
            return (
              !event?.id ||
              moment(value, "YYYY-MM-DDTHH:mm", true).isAfter(
                moment("2016-02-06", "YYYY-MM-DD", true) // first LVO
              )
            );
          }
        ),
      eventEndDate: Yup.string()
        .required("Required")
        .nullable()
        .test("Is valid date?", "Invalid event end date", (value) => {
          if (!value || value.trim() === "") {
            return false;
          }
          return moment(value, "YYYY-MM-DDTHH:mm", true).isValid();
        }),
    });

  // Only checks for errors on submit, then adds to eventValues object
  const submitDetails = async (values) => {
    try {
      if (eventId) {
        await submitEvent(values);
      } else {
        if (moment(values.eventDate).isAfter(moment(values.eventEndDate))) {
          let error = new Error(
            "Event Start Date must be before the Event End Date"
          );
          error.name = "Validation Error";
          throw error;
        }

        if (values.reoccurringEvent && values.reoccurringDates.length < 2) {
          let error = new Error(
            "You have selected Recurring Event but have not set any recurring dates."
          );
          error.name = "Validation Error";
          throw error;
        }

        if (!eventValues) {
          setEventValues(values);
        } else {
          setEventValues({ ...eventValues, ...values });
        }

        setStep(step + 1);
        window.scrollTo(0, 0);
        navigate(`${window.location.pathname}?active_tab=config`, {
          replace: false,
        });

        // TODO: Post process any values, then set eventValues equal to the new values. Overwrite duplicate values {...eventValues, ...sanitizedValues}
      }
    } catch (err) {
      postNotification("error", err);
    }
  };

  const submitConfig = async (values) => {
    try {
      if (eventId) {
        await submitEvent(values);
      } else {
        if (
          (values?.eventType === "Teams Event" || event?.teamEvent) &&
          !values?.teamScorecardId
        ) {
          let error = new Error("Please select a Team Scorecard.");
          error.name = "Validation Error";
          throw error;
        }

        if (!eventValues) {
          setEventValues(values);
        } else {
          setEventValues({ ...eventValues, ...values });
        }

        setStep(step + 1);
        window.scrollTo(0, 0);
        navigate(`${window.location.pathname}?active_tab=registration`, {
          replace: false,
        });
      }
    } catch (err) {
      postNotification("error", err);
    }
  };

  // Final submit with all post-processing of data
  const submitEvent = async (values) => {
    setProcessing(true);
    try {
      if (values.shippingDetails.requested) {
        const description = values?.shippingDetails?.description;

        if (description) {
          if (description.length < 3) {
            let error = new Error(
              "Shipping details description must be at least 3 characters"
            );
            error.name = "Validation Error";
            throw error;
          }
          if (description.length > 256) {
            let error = new Error(
              "Shipping details description must be less than 255 characters"
            );
            error.name = "Validation Error";
            throw error;
          }
        }
      }
      if (moment(values.eventDate).isAfter(moment(values.eventEndDate))) {
        let error = new Error(
          "Event Start Date must be before the Event End Date"
        );
        error.name = "Validation Error";
        throw error;
      }
      if (values.usingOnlineReg && values.numTickets <= 0) {
        let error = new Error(
          "You have selected Use Online Registration but not set a number of tickets available."
        );
        error.name = "Validation Error";
        throw error;
      }

      if (values.reoccurringEvent && values.reoccurringDates.length < 2) {
        let error = new Error(
          "You have selected Recurring Event but have not set any recurring dates."
        );
        error.name = "Validation Error";
        throw error;
      }

      let changeScorecard = false;

      if (event?.started) {
        if (
          (event.teamScorecardId &&
            values.teamScorecardId &&
            event.teamScorecardId !== values.teamScorecardId) ||
          (event.scorecardId &&
            values.scorecardId &&
            event.scorecardId !== values.scorecardId)
        ) {
          try {
            setProcessing(true);
            await confirm({
              title: "Update scorecards?",
              description:
                "You have changed the scorecard(s) for this event. Would you like to update any UNSUBMITTED pairing records for the active round? If there are SUBMITTED pairing records these will need to be manually updated.",
              dialogProps: {
                sx: {
                  zIndex: 99999,
                },
              },
              confirmationText: "Yes",
              cancellationText: "No",
            });

            changeScorecard = true;
          } catch {}
        }
      }

      var body;
      var sanitizedValues = {
        ...values,

        // Adds an extra \n for proper spacing if only one \n is detected. Ignores lines where list items are present as well as tables
        eventDescriptionMarkup: values?.eventDescriptionMarkup
          ? values.eventDescriptionMarkup.replace(
              /(?<!\n)(?<!^)(?<!\|)\n(?!\n)(?!$)(?!\|)(?!^\s*(\*|\d+\.\s))/gm,
              "\n\n"
            )
          : "",

        eventDescription: values?.eventDescriptionMarkup
          ? markdownToPlainText(values.eventDescriptionMarkup)
          : values?.eventDescription
            ? values.eventDescription
            : "",

        // handle timezones
        eventDate:
          values?.location?.timeZone || timeZone
            ? moment
                .tz(
                  values.eventDate,
                  "YYYY-MM-DDTHH:mm",
                  values?.location?.timeZone || timeZone
                )
                .utc()
                .format()
            : moment(values.eventDate, "YYYY-MM-DDTHH:mm").utc().format(),
        eventEndDate:
          values?.location?.timeZone || timeZone
            ? moment
                .tz(
                  values.eventEndDate,
                  "YYYY-MM-DDTHH:mm",
                  values?.location?.timeZone || timeZone
                )
                .utc()
                .format()
            : moment(values.eventEndDate, "YYYY-MM-DDTHH:mm").utc().format(),
        refundDate:
          values?.location?.timeZone || timeZone
            ? moment
                .tz(
                  values.refundDate,
                  "YYYY-MM-DDTHH:mm",
                  values?.location?.timeZone || timeZone
                )
                .utc()
                .format()
            : moment(values.refundDate, "YYYY-MM-DDTHH:mm").utc().format(),

        defaultRoundLength:
          values.defaultRoundLength.hrs * 60 * 60 +
          values.defaultRoundLength.mins * 60,
        ticketPrice: values.ticketPrice * 100,
      };

      if (copyEvent?.softScores?.length) {
        sanitizedValues.softScores = copyEvent.softScores;
        for (let i = 0; i < sanitizedValues.softScores.length; i++) {
          delete sanitizedValues.softScores[i].id;
        }
      }

      if (values.reoccurringEvent && values.reoccurringDates?.length > 0) {
        sanitizedValues.eventDate = null;
        sanitizedValues.eventEndDate = null;
        sanitizedValues.refundDate = null;

        for (let i = 0; i < sanitizedValues.reoccurringDates?.length; i++) {
          if (sanitizedValues.reoccurringDates[i].eventDate) {
            sanitizedValues.reoccurringDates[i].eventDate =
              values?.location?.timeZone || timeZone
                ? moment
                    .tz(
                      sanitizedValues.reoccurringDates[i].eventDate,
                      "YYYY-MM-DDTHH:mm",
                      values?.location?.timeZone || timeZone
                    )
                    .utc()
                    .format()
                : moment(
                    sanitizedValues.reoccurringDates[i].eventDate,
                    "YYYY-MM-DDTHH:mm"
                  )
                    .utc()
                    .format();
          }
          if (sanitizedValues.reoccurringDates[i].eventEndDate) {
            sanitizedValues.reoccurringDates[i].eventEndDate =
              values?.location?.timeZone || timeZone
                ? moment
                    .tz(
                      sanitizedValues.reoccurringDates[i].eventEndDate,
                      "YYYY-MM-DDTHH:mm",
                      values?.location?.timeZone || timeZone
                    )
                    .utc()
                    .format()
                : moment(
                    sanitizedValues.reoccurringDates[i].eventEndDate,
                    "YYYY-MM-DDTHH:mm"
                  )
                    .utc()
                    .format();
          }
          if (sanitizedValues.reoccurringDates[i].refundDate) {
            sanitizedValues.reoccurringDates[i].refundDate =
              values?.location?.timeZone || timeZone
                ? moment
                    .tz(
                      sanitizedValues.reoccurringDates[i].refundDate,
                      "YYYY-MM-DDTHH:mm",
                      values?.location?.timeZone || timeZone
                    )
                    .utc()
                    .format()
                : moment(
                    sanitizedValues.reoccurringDates[i].refundDate,
                    "YYYY-MM-DDTHH:mm"
                  )
                    .utc()
                    .format();
          }
        }
      } else {
        delete sanitizedValues.reoccurringDates;
      }

      delete sanitizedValues.eventType;
      delete sanitizedValues.allowTextOnly;

      if (!templates?.length || values?.templateId === "default") {
        delete sanitizedValues.templateId;
        delete sanitizedValues.templateName;
        delete sanitizedValues.templateModified;
      }

      if (!authContext?.authState?.identity?.isAdmin) {
        delete sanitizedValues.sponsored;
        if (
          !authContext?.authState?.identity?.adminGameSystems?.includes(
            event?.gameSystemId || copyEvent?.gameSystemId || gameSystem?.id
          )
        ) {
          delete sanitizedValues.featured;
        }
      }

      if (!sanitizedValues.placingRecordType?.key) {
        delete sanitizedValues.placingRecordType;
      }

      if (!sanitizedValues.teamEvent || sanitizedValues.doublesEvent) {
        delete sanitizedValues.playerPlacingMetrics;
        delete sanitizedValues.teamScorecardId;
      }
      if (!sanitizedValues.playerPlacingMetrics?.length) {
        delete sanitizedValues.playerPlacingMetrics;
      }
      if (!sanitizedValues.placingMetrics?.length) {
        delete sanitizedValues.placingMetrics;
      }
      if (!sanitizedValues.pairingMetrics?.length) {
        delete sanitizedValues.pairingMetrics;
      }

      if (sanitizedValues.pairingStyle?.toLowerCase() !== "swiss") {
        delete sanitizedValues.numberOfRounds;
      }

      if (
        !globalVars.eventSubTypes?.list?.length &&
        sanitizedValues.eventSubType
      ) {
        delete sanitizedValues.eventSubType;
      }

      if (sanitizedValues?.photoFileId === null) {
        delete sanitizedValues.photoFileId;
      }

      if (sanitizedValues.eventImage) {
        // upload image to s3
        var newFile = await uploadBas64ToS3(sanitizedValues.eventImage);
        delete sanitizedValues.eventImage;
        if (newFile.fileId) {
          sanitizedValues.photoFileId = newFile.fileId;
        }
      }

      if (event?.id) {
        // update
        body = {
          set: sanitizedValues,
        };

        var unset = {};

        if (!sanitizedValues.placingRecordType?.key) {
          unset.placingRecordType = true;
        }

        if (
          !sanitizedValues.eventFormat ||
          sanitizedValues.eventFormat === ""
        ) {
          delete body.set.eventFormat;
          unset.eventFormat = true;
        }

        if (
          !sanitizedValues.scorecardId ||
          sanitizedValues.scorecardId === "" ||
          sanitizedValues.scorecardId === "Default Scorecard" ||
          sanitizedValues.scorecardId === event?.gameSystem?.scorecardId
        ) {
          delete body.set.scorecardId;
          unset.scorecardId = true;
        }

        if (
          !sanitizedValues.teamScorecardId ||
          sanitizedValues.teamScorecardId === "" ||
          sanitizedValues.teamScorecardId === "Default Scorecard" ||
          sanitizedValues.teamScorecardId === event?.gameSystem?.teamScorecardId
        ) {
          delete body.set.teamScorecardId;
        }

        if (!sanitizedValues.usingOnlineReg) {
          //unset.refundDate = "";
          delete body.set.refundDate;
        }
        if (Object.keys(unset).length > 0) {
          body.unset = unset;
        }

        var apiEvent = await postData(`/events/${event.id}`, {
          body: body,
        });

        if (changeScorecard) {
          await postData(`/events/${event.id}/resetScorecards`, {});
        }

        setEvent({
          ...event,
          ...apiEvent,
        });
        postNotification("success", "Event Updated");
      } else {
        // new
        if (copyId) {
          sanitizedValues.copyEventId = copyId;
        }
        sanitizedValues.gameType = copyEvent
          ? copyEvent.gameType
          : gameSystem.code;
        sanitizedValues.gameSystemId = copyEvent
          ? copyEvent.gameSystemId
          : gameSystem.id;
        sanitizedValues.eventSubType = copyEvent
          ? copyEvent.eventSubType
          : eventSubType;
        if (sanitizedValues.eventFormat === "") {
          delete sanitizedValues.eventFormat;
        }
        sanitizedValues.boardGameEvent = copyEvent
          ? copyEvent.boardGameEvent
          : gameSystem.boardGameEvent;
        sanitizedValues.ownerId = authContext?.authState?.identity?.userId;

        if (copyEvent?.gameStore?.id) {
          sanitizedValues.gameStoreId = copyEvent.gameStore.id;
        } else if (gameStore?.id) {
          sanitizedValues.gameStoreId = gameStore.id;
        }

        body = sanitizedValues;

        if (
          !body.scorecardId ||
          body.scorecardId === "" ||
          body.scorecardId === "Default Scorecard" ||
          body.scorecardId === gameSystem.scorecardId
        ) {
          delete body.scorecardId;
        }
        if (
          !body.teamScorecardId ||
          body.teamScorecardId === "" ||
          body.teamScorecardId === "Default Scorecard" ||
          body.teamScorecardId === gameSystem.teamScorecardId
        ) {
          delete body.teamScorecardId;
        }

        if (!body.usingOnlineReg) {
          delete body.refundDate;
        }

        var apiData = await postData("/events", {
          body: body,
        });

        if (apiData && !apiData?.error) {
          // Check if there are circuits that need to request tokens for. If so, requests them here
          const requestTokens = async () => {
            if (circuitsRequested?.length) {
              try {
                await Promise.all(
                  circuitsRequested.map(async (circuitId) => {
                    const newToken = {
                      userId: authContext?.authState?.identity?.userId,
                      email: authContext?.authState?.identity?.email,
                      eventId: apiData.id,
                      leagueId: circuitId,
                    };
                    await postData(`/leaguetokens`, newToken);
                  })
                );
              } catch (err) {
                postNotification("error", "Error requesting circuit tokens");
                console.error(err);
              }
            }
          };

          requestTokens().then(() => {
            postNotification("success", "Event Created");
            setTimeout(() => {
              window.location.assign(
                `/organize/event/${apiData.id}?active_tab=roster`
              );
            }, 1500);
          });
        } else {
          throw new Error(apiData?.error || "Failed to create event");
        }
      }
    } catch (err) {
      postNotification(
        "error",
        err.name === "Validation Error" ? err.message : err
      );
    }
    setProcessing(false);
    return;
  };

  // Prompt for user that pops up if the user is trying to modify the template settings
  const templateChangesPrompt = async (values, setFieldValue) => {
    if (
      templates &&
      templates?.length &&
      values?.templateId &&
      !values?.templateModified
    ) {
      try {
        await confirm({
          title: "Edit scoring template",
          description: `If you make changes, you will no longer be using the ${
            values?.templateName || "default"
          } template. Your template above will change to "Custom". Do you want to continue?`,
          dialogProps: {
            sx: {
              zIndex: 99999,
            },
          },
        });

        setFieldValue("templateModified", true);
      } catch (err) {
        return;
      }
    }
  };

  useEffect(() => {
    if (gameSystem) {
      postGameSystem(gameSystem);

      const go = async () => {
        try {
          let apiTemplates = await getEventTemplates(gameSystem?.id);

          if (apiTemplates) setTemplates(apiTemplates);
        } catch (err) {
          postNotification("error", err);
        }
      };

      go();
    }
    // eslint-disable-next-line
  }, [gameSystem]);

  // Creates "default" template and updates custom template values to match with eventValues
  useEffect(() => {
    if (eventValues && templates && templates.length) {
      let tempTemplates = [...templates];

      if (templates[0]?.id !== "default") {
        const defaultValues = {
          name: "Default (recommended)",
          id: "default",
          templateDescription: `These are the standard settings for ${
            gameSystem?.name || "this game system"
          }.`,
          templateType: officialTemplateType,
          gameSystem: { ...gameSystem },
          pairingMetrics: gameSystem?.defaultPairingsMetrics || [],
          placingMetrics: gameSystem?.defaultPlacingMetrics || [],
          playerPlacingMetrics: [],
          scorecard: {
            id: gameSystem?.scorecardId || "",
            name:
              scorecardDict?.[gameSystem?.scorecardId]?.name ||
              `${gameSystem?.name} Default`,
          },
        };

        tempTemplates.unshift(defaultValues);
      }

      const customValues = {
        name: "Custom",
        id: "custom",
        templateDescription: `These are the currently selected custom settings for ${
          gameSystem?.name || "this game system"
        }.`,
        templateType: otherTemplateType,
        gameSystem: { ...gameSystem },
        pairingMetrics: eventValues?.pairingMetrics || [],
        placingMetrics: eventValues?.placingMetrics || [],
        playerPlacingMetrics: eventValues?.playerPlacingMetrics || [],
        scorecard: {
          id: eventValues?.scorecardId,
          name:
            scorecardDict?.[eventValues?.scorecardId]?.name ||
            `Custom${" " + gameSystem?.name} Scorecard`,
        },
      };

      if (templates?.[templates.length - 1]?.id === "custom")
        tempTemplates.pop();

      tempTemplates.push(customValues);

      tempTemplates.sort((a, b) => {
        if (a.templateType?.sort && !b.templateType?.sort) return -1;
        if (!a.templateType?.sort && b.templateType?.sort) return 1;
        if (a.templateType?.sort < b.templateType?.sort) return -1;
        if (a.templateType?.sort > b.templateType?.sort) return 1;

        var labelA = a.templateType?.label?.toLowerCase();
        var labelB = b.templateType?.label?.toLowerCase();
        if (labelA > labelB) return 1;
        if (labelA < labelB) return -1;

        var nameA = a.label?.toLowerCase();
        var nameB = b.label?.toLowerCase();
        if (nameA > nameB) return 1;
        if (nameA < nameB) return -1;
        return 0;
      });

      setTemplates(tempTemplates);
    }

    // eslint-disable-next-line
  }, [eventValues]);

  // Adds gamestore location to saved locations if one is present
  useEffect(() => {
    if ((gameStore || event?.gameStore) && locations) {
      let gameStoreLoc =
        gameStore?.location || event?.gameStore?.location || null;

      if (gameStoreLoc) {
        gameStoreLoc.name = gameStore?.name || event?.gameStore?.name;

        if (gameStoreLoc !== locations[0]) {
          setLocations([gameStoreLoc, ...locations]);
        }
      }
    }
    // eslint-disable-next-line
  }, [gameStore, locations]);

  useEffect(() => {
    if (
      step === 1 &&
      (getEnvironmentVariable("SINGLE_GAME_SYSTEM") || gameSystem) &&
      (!globalVars?.eventSubTypes?.list?.length || eventSubType)
    ) {
      setStep(2);
      window.scrollTo(0, 0);
      navigate(`${window.location.pathname}?active_tab=details`, {
        replace: false,
      });
    }
    // eslint-disable-next-line
  }, [gameSystem, eventSubType]);

  useEffect(() => {
    const tempAccess =
      ((event?.gameStore?.id || event?.gameStoreId || gameStore?.id) &&
        (event?.gameStore?.bankAccount?.id || gameStore?.bankAccount?.id)) ||
      (!event?.gameStore?.id && !gameStore?.id && user?.bankAccount) ||
      (!event && !gameStore && user?.bankAccount) ||
      ((event?.numTickets || event?.ticketingAvailable) &&
        event?.ticketPrice > 0);

    if (tempAccess) setHasTicketingAccess(tempAccess);
  }, [event, gameStore, user]);

  useEffect(() => {
    pageSetup();
    // eslint-disable-next-line
  }, []);

  if (loading) {
    return <ModalLoadingFull />;
  }

  return (
    <>
      {IS_UY && gameStores?.length === 0 ? (
        <EventCreationError />
      ) : (
        <EventCreationDefault
          event={event}
          setEvent={setEvent}
          eventId={eventId}
          loading={loading}
          step={step}
          setStep={setStep}
          gameStore={gameStore}
          gameStores={gameStores}
          authContext={authContext}
          setEventSubType={setEventSubType}
          setGameStore={setGameStore}
          gameStoreDict={gameStoreDict}
          globalVars={globalVars}
          gameSystem={gameSystem}
          gameSystems={gameSystems}
          circuits={circuits}
          setCircuits={setCircuits}
          setGameSystem={setGameSystem}
          eventSubType={eventSubType}
          copyEvent={copyEvent}
          user={user}
          processing={processing}
          setProcessing={setProcessing}
          markdownToPlainText={markdownToPlainText}
          timeZone={timeZone}
          setTimeZone={setTimeZone}
          copyId={copyId}
          readOnly={readOnly}
          setReadOnly={setReadOnly}
          locations={locations}
          setLocations={setLocations}
          reoccurringOpen={reoccurringOpen}
          setReoccurringOpen={setReoccurringOpen}
          scorecards={scorecards}
          scorecardDict={scorecardDict}
          teamScorecards={teamScorecards}
          setScorecardAdvancedOpen={setScorecardAdvancedOpen}
          onDragEnd={onDragEnd}
          headerRef={headerRef}
          headerInView={headerInView}
          EventHeader={EventHeader}
          scorecardAdvancedOpen={scorecardAdvancedOpen}
          ScorecardAdvancedModal={ScorecardAdvancedModal}
          getInitialValues={getInitialValues}
          getValidationSchema={getValidationSchema}
          submitEvent={submitEvent}
          EventStepper={EventStepper}
          navigate={navigate}
          getDetailsValidationSchema={getDetailsValidationSchema}
          submitDetails={submitDetails}
          isCustom={isCustom}
          setIsCustom={setIsCustom}
          getConfigValidationSchema={getConfigValidationSchema}
          submitConfig={submitConfig}
          theme={theme}
          confirm={confirm}
          hasSubAccess={hasSubAccess}
          hasTicketingAccess={hasTicketingAccess}
          ticketingDict={ticketingDict}
          eventValues={eventValues}
          setEventValues={setEventValues}
          templates={templates}
          setTemplates={setTemplates}
          template={template}
          setTemplate={setTemplate}
          tempModalOpen={tempModalOpen}
          setTempModalOpen={setTempModalOpen}
          TemplateModal={TemplateModal}
          templateChangesPrompt={templateChangesPrompt}
          scorecardModalOpen={scorecardModalOpen}
          setScorecardModalOpen={setScorecardModalOpen}
          ScorecardModal={ScorecardModal}
          circuitsRequested={circuitsRequested}
          setCircuitsRequested={setCircuitsRequested}
        />
      )}
    </>
  );
}

function ScorecardAdvancedModal(props) {
  const { open, setOpen, scorecard, values, setFieldValue, disabled } = props;
  const [defaultByeGameData, setDefaultByeGameData] = useState(null);
  useEffect(() => {
    if (!scorecard?.defaultByeGameData?.length) {
      setOpen(false);
    } else {
      let defaultByeGameDataObj = {};
      for (let metric of scorecard.defaultByeGameData) {
        defaultByeGameDataObj[metric.metric] = metric.default || 0;
      }
      setDefaultByeGameData(values.defaultByeGameData || defaultByeGameDataObj);
    }
    // eslint-disable-next-line
  }, [props]);

  const handleChange = (e) => {
    if (!e?.target?.name) return;
    if (defaultByeGameData[e.target.name] === e.target.value) return;
    setDefaultByeGameData({
      ...defaultByeGameData,
      [e.target.name]: validateMetric(e.target.value),
    });
  };

  return (
    <Modal
      disableAutoFocus
      open={open}
      onClose={() => {
        setOpen(false);
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: ["95vw", "85vw", 600],
          //minHeight: "95vh",
          maxHeight: "95vh",
          overflowY: "scroll",
          bgcolor: "background.paper",
          border: "1px solid rgba(0, 0, 0, 0.12)",
          borderRadius: 1,
          boxShadow: 24,
          p: 4,
        }}
      >
        {Boolean(defaultByeGameData) && (
          <>
            <IconButton
              aria-label="cancel"
              color="error"
              sx={{ position: "absolute", top: "4px", right: "4px" }}
              onClick={() => {
                setOpen(false);
              }}
            >
              <BCPXmark />
            </IconButton>
            <Grid
              container
              spacing={2}
              sx={{
                pb: 1,
                mb: 2,
                borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
              }}
            >
              <Grid item xs={12}>
                <Typography variant="h4" component="h2">
                  Advanced Scorecard Features
                </Typography>
              </Grid>
            </Grid>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Typography variant="h5" component="h2">
                  Default Bye Values
                </Typography>
              </Grid>
              {scorecard.defaultByeGameData?.map((metric) => {
                if (metric.options?.length) {
                  return (
                    <GenericPicker
                      key={metric.metric}
                      name={metric.metric}
                      label={metric.label}
                      value={defaultByeGameData[metric.metric]}
                      handleChange={handleChange}
                      list={metric.options}
                      inclAny={false}
                    />
                  );
                }
                return (
                  <TextField
                    key={metric.metric}
                    name={metric.metric}
                    label={metric.label}
                    value={defaultByeGameData[metric.metric]}
                    variant="outlined"
                    fullWidth
                    required
                    margin="normal"
                    type="number"
                    onBlur={handleChange}
                    onChange={handleChange}
                    disabled={disabled}
                  />
                );
              })}

              {!disabled && (
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    onClick={(e) => {
                      setFieldValue("defaultByeGameData", defaultByeGameData);
                      setOpen(false);
                    }}
                  >
                    Save
                  </Button>
                </Grid>
              )}
            </Grid>
          </>
        )}
      </Box>
    </Modal>
  );
}

function TemplateModal(props) {
  const { open, setOpen, template } = props;

  return (
    <Modal
      disableAutoFocus
      open={open}
      onClose={() => {
        setOpen(false);
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "85vw",
          maxHeight: "85vh",
          overflowY: "scroll",
          bgcolor: "background.paper",
          border: "1px solid rgba(0, 0, 0, 0.12)",
          borderRadius: 1,
          boxShadow: 24,
          p: 4,
          zIndex: 99999,
        }}
      >
        <IconButton
          aria-label="cancel"
          color="error"
          sx={{ position: "absolute", top: "4px", right: "4px" }}
          onClick={() => {
            setOpen(false);
          }}
        >
          <BCPXmark />
        </IconButton>
        <Grid
          container
          spacing={2}
          sx={{
            pb: 1,
            mb: 2,
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
          }}
        >
          <Grid item xs={12}>
            <Typography variant="h3" component="h2">
              {template?.name}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body1">
              {template?.templateDescription || "------"}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography
              variant="h4"
              gutterBottom
              sx={{ textDecoration: "underline" }}
            >
              Scorecard
            </Typography>
            <Typography variant="body1">
              {template?.scorecard?.name || "------"}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography
              variant="h4"
              gutterBottom
              sx={{ textDecoration: "underline" }}
            >
              Pairing Metrics (who plays who next)
            </Typography>
            <ol style={{ fontSize: "16px", paddingLeft: "24px" }}>
              {template?.pairingMetrics.map((metric) =>
                metric?.isOn ? <li>{metric?.name}</li> : <></>
              )}
            </ol>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography
              variant="h4"
              gutterBottom
              sx={{ textDecoration: "underline" }}
            >
              Placing Metrics (order of tie breaking)
            </Typography>
            <ol style={{ fontSize: "16px", paddingLeft: "24px" }}>
              {template?.placingMetrics.map((metric) =>
                metric?.isOn ? <li>{metric?.name}</li> : <></>
              )}
            </ol>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
}

function EventStepper(props) {
  const { step, setStep, circuits, event } = props;

  const theme = useTheme();
  const navigate = useNavigate();

  const form = document.querySelector("form");

  const boxStyles = {
    display: "flex",
    flexDirection: "column",
    width: "33%",
    justifyContent: "center",
    alignItems: "center",
    gap: 1,
    position: "relative",
  };

  const iconWrapperStyles = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  };

  const iconStyles = (iconStep) => ({
    fontSize: "24px",
    width: "24px",
    cursor: "pointer",
    color:
      step === iconStep
        ? theme?.palette?.link.main
        : event?.id
          ? theme?.palette?.success.main
          : step < iconStep
            ? theme?.palette?.text?.disabled
            : theme?.palette?.success.main,
  });

  const lineStyles = {
    content: '""',
    position: "absolute",
    left: "calc(50% + 5px)",
    top: "20%",
    height: "2px",
    width: "95%",
    zIndex: -1,
  };

  return (
    <Container
      sx={{
        display: "flex",
        alignItems: "start",
        justifyContent: "center",
        textAlign: "center",
        position: "relative",
        width: "100%",
        mb: 2,
      }}
    >
      <Box sx={{ ...boxStyles }}>
        <Box
          sx={{
            ...lineStyles,
            backgroundColor:
              step >= 5 || event?.id
                ? theme?.palette?.success.main
                : theme?.palette?.text?.disabled,
          }}
        />
        <Box
          onClick={() => {
            setStep(2);
            navigate(`${window.location.pathname}?active_tab=details`, {
              replace: false,
            });
            window.scrollTo(0, 0);
          }}
          sx={{ ...iconWrapperStyles }}
        >
          <BCPCircle1 sx={iconStyles(2)} />
          <span style={{ cursor: "pointer" }}>Event Details</span>
        </Box>
      </Box>

      <Box sx={{ ...boxStyles }}>
        <Box
          sx={{
            ...lineStyles,
            top: ["15%", "20%"],
            backgroundColor:
              step === 4 || event?.id
                ? theme?.palette?.success.main
                : theme?.palette?.text?.disabled,
          }}
        />
        <Box
          onClick={() => {
            if (!event?.id && step === 2) {
              form.requestSubmit();
            } else {
              setStep(3);
            }
            navigate(`${window.location.pathname}?active_tab=config`, {
              replace: false,
            });
            window.scrollTo(0, 0);
          }}
          sx={{ ...iconWrapperStyles }}
        >
          <BCPCircle2 sx={iconStyles(3)} />
          <span style={{ cursor: "pointer" }}>Game Configuration</span>
        </Box>
      </Box>

      <Box sx={{ ...boxStyles, mt: !circuits?.length ? -1 : 0 }}>
        <Box
          sx={{
            ...(circuits?.length ? lineStyles : ""),
            backgroundColor:
              step === 4 || event?.id
                ? theme?.palette?.success.main
                : theme?.palette?.text?.disabled,
          }}
        />
        <Box
          onClick={() => {
            if (!event?.id && (step === 2 || step === 3)) {
              form.requestSubmit();
            } else {
              setStep(4);
            }
            navigate(`${window.location.pathname}?active_tab=registration`, {
              replace: false,
            });
            window.scrollTo(0, 0);
          }}
          sx={{
            ...iconWrapperStyles,
          }}
        >
          <BCPCircle3 sx={iconStyles(4)} />
          <span style={{ cursor: "pointer" }}>Registration & Tickets</span>
        </Box>
      </Box>

      {circuits?.length ? (
        <Box sx={{ ...boxStyles }}>
          <Box
            onClick={() => {
              if (!event?.id && (step === 2 || step === 3 || step === 4)) {
                form.requestSubmit();
              } else {
                setStep(5);
              }
              navigate(`${window.location.pathname}?active_tab=circuits`, {
                replace: false,
              });
              window.scrollTo(0, 0);
            }}
            sx={{ ...iconWrapperStyles }}
          >
            <BCPCircle4 sx={iconStyles(5)} />
            <span style={{ cursor: "pointer" }}>Circuits</span>
          </Box>
        </Box>
      ) : (
        <></>
      )}
    </Container>
  );
}

function EventHeader(props) {
  const {
    step,
    setStep,
    event,
    copyId,
    gameSystem,
    gameStores,
    confirm,
    navigate,
    setCircuits,
    circuits,
  } = props;

  const uiBackButtons = [
    // No nav ui button is rendered on step 0 so this first one is intentionally left blank. If we ever decide to add a nav button to step 0 in the future then replace this one
    {
      label: "",
      onClick: () => {},
      step: 0,
    },
    {
      label: "Select an Organization",
      onClick: () => {
        setStep(0);
        window.scrollTo(0, 0);
      },
      step: 1,
    },
    {
      label: "Select Game System",
      onClick: () => {
        setStep(1);
        setCircuits(null);
        window.scrollTo(0, 0);
      },
      step: 2,
    },
    {
      label: "Event Details",
      onClick: () => {
        navigate(`${window.location.pathname}?active_tab=details`, {
          replace: false,
        });
        setStep(2);
        window.scrollTo(0, 0);
      },
      step: 3,
    },
    {
      label: "Game Configuration",
      onClick: () => {
        navigate(`${window.location.pathname}?active_tab=config`, {
          replace: false,
        });
        setStep(3);
        window.scrollTo(0, 0);
      },
      step: 4,
    },
    {
      label: "Registration & Tickets",
      onClick: () => {
        navigate(`${window.location.pathname}?active_tab=registration`, {
          replace: false,
        });
        setStep(4);
        window.scrollTo(0, 0);
      },
      step: 5,
    },
  ];

  return (
    <Grid container>
      <Grid
        item
        xs={12}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          color: "link.main",
        }}
      >
        <Typography
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
          }}
          onClick={uiBackButtons?.[step]?.onClick}
        >
          {/* Left side buttons */}
          {((step !== 0 && !event?.id) || (step > 2 && event?.id)) && (
            <>
              {(step === 1 && !gameStores?.length) || (step === 2 && copyId) ? (
                <></>
              ) : (
                <>
                  <BCPAngleLeft
                    style={{ "font-size": "16px", width: "16px" }}
                  />
                  {uiBackButtons?.[step]?.label}
                </>
              )}
            </>
          )}
        </Typography>

        {/* Right side buttons */}
        <Typography
          sx={{ cursor: "pointer" }}
          onClick={async () => {
            if (!event?.id || copyId) {
              try {
                await confirm({
                  title: "Changes will be lost",
                  description:
                    "Are you sure you want to stop creating your event?",
                  dialogProps: { sx: { zIndex: 99999 } },
                });

                navigate(`/organize/events`);
              } catch (err) {
                return;
              }
            } else {
              window.scrollTo(0, 0);

              if (step === 2) {
                setStep(3);
                navigate(`${window.location.pathname}?active_tab=config`, {
                  replace: false,
                });
              }

              if (step === 3) {
                setStep(4);
                navigate(
                  `${window.location.pathname}?active_tab=registration`,
                  {
                    replace: false,
                  }
                );
              }

              if (step === 4) {
                setStep(5);
                navigate(`${window.location.pathname}?active_tab=circuits`, {
                  replace: false,
                });
              }
            }
          }}
        >
          {!event?.id || copyId
            ? "Cancel"
            : step === 2
              ? "Game Configuration"
              : step === 3
                ? "Registration & Tickets"
                : step === 4 && (event?.circuits?.length || circuits?.length)
                  ? "Circuits"
                  : ""}
          {event?.id &&
          !copyId &&
          (step === 2 ||
            step === 3 ||
            (step === 4 && (event?.circuits?.length || circuits?.length))) ? (
            <BCPAngleRight style={{ "font-size": "16px", width: "16px" }} />
          ) : (
            <></>
          )}
        </Typography>
      </Grid>

      <Grid item xs={12} sx={{ pt: 1 }}>
        <Typography variant="h3" gutterBottom sx={{ textAlign: "center" }}>
          {step === 0
            ? "Select an Organization"
            : step === 1
              ? "Select Game System"
              : (step === 2 || step === 3 || step === 4 || step === 5) &&
                  gameSystem
                ? gameSystem?.name
                : ""}
        </Typography>
      </Grid>
      {(step === 0 || step === 1) && (
        <Grid item xs={12}>
          <Typography variant="body1" sx={{ textAlign: "center" }}>
            {step === 0
              ? "What organization is hosting this event?"
              : step === 1
                ? "What game system would you like to set up your event for?"
                : ""}
          </Typography>
        </Grid>
      )}
    </Grid>
  );
}

function ScorecardModal(props) {
  const { open, setOpen, scorecardId, event, gameSystem } = props;

  const [scorecard, setScorecard] = useState(null);
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    if (scorecardId && scorecardId !== "") {
      const fetchScorecard = async () => {
        try {
          const apiScorecard = await getData(`/scorecards/${scorecardId}`);

          setScorecard(apiScorecard);
        } catch (err) {
          postNotification("error", err);
        } finally {
          setProcessing(false);
        }
      };

      fetchScorecard();
    }
  }, [scorecardId]);

  useEffect(() => {
    if (!event?.gameSystem) {
      if (gameSystem) event.gameSystem = gameSystem;
    }
    // eslint-disable-next-line
  }, []);

  return (
    <Modal
      disableAutoFocus
      open={open}
      onClose={() => {
        setOpen(false);
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "85vw",
          maxHeight: "85vh",
          overflowY: "scroll",
          bgcolor: "background.paper",
          border: "1px solid rgba(0, 0, 0, 0.12)",
          borderRadius: 1,
          boxShadow: 24,
          p: 4,
          zIndex: 99999,
        }}
      >
        <IconButton
          aria-label="cancel"
          color="error"
          sx={{ position: "absolute", top: "4px", right: "4px" }}
          onClick={() => {
            setOpen(false);
          }}
        >
          <BCPXmark />
        </IconButton>
        <Grid
          container
          spacing={2}
          sx={{
            pb: 1,
            mb: 2,
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
          }}
        >
          <Grid
            container
            spacing={2}
            sx={{
              pb: 1,
              mb: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            }}
          >
            <Grid item xs={12}>
              <Typography variant="h3" gutterBottom>
                Scorecard Preview
              </Typography>
              <Typography variant="h4">{scorecard?.name || "-----"}</Typography>
              <Typography variant="caption">
                {`Below is a preview of the scorecard ${
                  scorecard?.name || ""
                } that will be used for pairings in ${
                  event?.name?.trim() || "the current event"
                }.`}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {!scorecardId ? (
              <Grid item xs={12}>
                <Typography variant="body1">
                  {`Please select a scorecard to preview.`}
                </Typography>
              </Grid>
            ) : !scorecard && !processing ? (
              <Grid item xs={12}>
                <Typography variant="body1">
                  {`Scorecard preview is unavailable at this time for the selected scorecard.`}
                </Typography>
              </Grid>
            ) : scorecard ? (
              <Grid item xs={12} sx={{ mt: -8 }}>
                <GameRouter preview event={event} scorecard={scorecard} />
              </Grid>
            ) : (
              <></>
            )}
            {processing && scorecardId && (
              <Grid item xs={12}>
                <LinearProgress />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
}

function EventCreationError() {
  useEffect(() => {
    postNotification("warning", "Subscription access required.");
  }, []);

  return (
    <Container sx={{ pb: 2, pt: 2 }} maxWidth="lg">
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Typography variant="h3" gutterBottom>
            Event Creation Error
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body1" gutterBottom>
            {`You are currently unable to create or modify any events.`}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body1">
            You must have an active{" "}
            <Link href="/subscription">player subscription</Link>
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body1" sx={{ fontWeight: "700" }}>
            OR
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body1" gutterBottom>
            {`You must be an owner/authorized tournament organizer of a game store with an active game store subscription`}
          </Typography>
        </Grid>
      </Grid>
    </Container>
  );
}
